import React, {useCallback, useEffect, useMemo} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {FilterState} from '../../../../components/Schedule/header/components/filter/types';
import {Year} from '../../../../features/settings/workcalendar/types';
import moment from 'moment';
import {
    Hours,
    TimeSheetGroupType
} from '../../../../features/schedule/types';
import {useTimeSheetSearch} from '../../contexts/search';
import {useTimeSheetGroupBy} from '../../hooks/useTimeSheetGroupBy';
import {updateTimeTrackingDateRangeFilter, updateTimeTrackingPropertyFilter} from '../../../../features/time-tracking-filters';
import styles from './schedule-top-bar.module.scss';
import {Box, Button, IconButton, Tooltip} from '@mui/material';
import ViewSelector from '../../../../components/Schedule/header/components/schedule-type-selector';
import ScheduleDateSelector from '../../../../components/Schedule/header/components/schedule-date-selector';
import {createPortal} from 'react-dom';
import {Lx} from '../../../../i18n/consts';
import {SVGS} from '../../../../assets/images';
import Filter from '../../../../components/Schedule/header/components/filter';
import TotalV2 from '../../../../components/Schedule/total-v2';
import TimeSheetGroupTool from '../../../../components/Schedule/timesheet-group-tool';
import {useAppSelector} from '../../../../hooks/hooks';
import {DayType, RangeType} from '../../../../components/shared/calendar/types';
import { useMediaQuery } from 'react-responsive';
import classNames from 'classnames';
import { initialStatuses } from '../../../../features/schedule/slice';
import {TiminatorAbsenceType, TiminatorType} from '../../../../features/types/types';
import SearchInput from '../../../../components/shared/search-input';
import TimeSheetMobileFilterSheet from './component/mobile-filter-sheet';
import {REmployees} from '../../../../features/roles/types';
import {PIdNameModel} from '../../../../features/projects/types';

interface Props {
    years: Year[];
    today: moment.Moment;
    loading: boolean;
    dataLoading?: boolean;
    summaryHours: Hours;
    onCreateTimeSheetItemClick: () => void;
    onImportClick?: () => void;
    onFavoritesClick?: () => void;
    canCreateTimeSheetItem?: boolean;
    canImport?: boolean;
    mobileFiltersOpen?: boolean;
    onMobileFilterVisibilityChange?: (open: boolean) => void;
    onMobileDateSelectorVisibilityChange?: (open: boolean) => void;
    subheaderPortalContainer?: HTMLElement | null;
    filterEmployees?: REmployees[];
    filterProjects?: PIdNameModel[];
    filterProjectTypes?: TiminatorType[];
    filterShiftTypes?: TiminatorType[];
    filterAbsenceTypes?: TiminatorAbsenceType[];
    disableEmployeesFilter?: boolean;
}

export const TopBar = ({
    years,
    today,
    summaryHours,
    onCreateTimeSheetItemClick,
    loading,
    dataLoading = false,
    onImportClick,
    onFavoritesClick,
    canCreateTimeSheetItem = true,
    canImport = true,
    mobileFiltersOpen = false,
    onMobileFilterVisibilityChange,
    onMobileDateSelectorVisibilityChange,
    subheaderPortalContainer,
    filterEmployees,
    filterProjects,
    filterProjectTypes,
    filterShiftTypes,
    filterAbsenceTypes,
    disableEmployeesFilter
}: Props) => {
    const intl = useIntl();
    const { change: searchChange, value: search } = useTimeSheetSearch();
    const { change: groupByChange, groupBy } = useTimeSheetGroupBy();

    const dateRangeStore = useAppSelector(s => s.timeTrackingFilters.filter?.dateRangeFilter);
    const entitiesFilters   = useAppSelector(s => s.timeTrackingFilters.filter?.propertyFilter);
    const isMobile = useMediaQuery({ maxWidth: 1000 });

    const ready = loading === undefined || !loading;
    const controlsDisabled = false;
    const allowedRangeTypes = useMemo(() => ([
        RangeType.CurrentMonth,
        RangeType.PreviousMonth,
        RangeType.ThisWeek,
        RangeType.LastWeek,
        RangeType.Custom,
    ]), []);
    const defaults = useMemo(() => ({
        startDate: today.clone().startOf('month'),
        endDate: today.clone().endOf('month')
    }), [today.valueOf()]);

    const selectedDates = useMemo(() => {
        const s = dateRangeStore?.startDate
            ? moment(dateRangeStore.startDate, 'YYYY-MM-DD')
            : defaults.startDate;
        const e = dateRangeStore?.endDate
            ? moment(dateRangeStore.endDate,   'YYYY-MM-DD')
            : defaults.endDate;
        return [s, e] as moment.Moment[];
    }, [dateRangeStore, defaults]);

    const hasDate = useMemo(() => {
        const [s, e] = selectedDates;
        return !(s.isSame(defaults.startDate, 'day') && e.isSame(defaults.endDate, 'day'));
    }, [selectedDates, defaults]);

    const hasSelectedEntities =
        (entitiesFilters?.selectedEmployees?.length ?? 0) > 0 ||
        (entitiesFilters?.selectedProjects?.length ?? 0) > 0 ||
        (entitiesFilters?.selectedProjectTypes?.length ?? 0) > 0 ||
        (entitiesFilters?.selectedShiftTypes?.length ?? 0) > 0 ||
        (entitiesFilters?.selectedAbsenceTypes?.length ?? 0) > 0 ||
        (entitiesFilters?.selectedStatuses?.length ?? 0) > 0 ||
        entitiesFilters?.showAbsences === false;

    const hasSearch = (search?.trim()?.length ?? 0) > 0;
    const hasAnyFilters = hasSearch || hasDate || hasSelectedEntities;

    const handleDateRangeChange = useCallback((values: moment.Moment[]) => new Promise<void>(() => {
        updateTimeTrackingDateRangeFilter({
            startDate: values[0].format('YYYY-MM-DD'),
            endDate:   values[1].format('YYYY-MM-DD'),
        });
    }), []);

    const handlePropertyFilterChanged = useCallback((value: FilterState) => new Promise<void>(() => {
        updateTimeTrackingPropertyFilter({
            selectedProjects: value.selectedProjects ?? [],
            selectedProjectTypes: value.selectedProjectTypes ?? [],
            selectedEmployees: value.selectedEmployees ?? [],
            selectedShiftTypes: value.selectedShiftTypes ?? [],
            selectedAbsenceTypes: value.selectedAbsenceTypes ?? [],
            selectedStatuses: value.selectedStatuses ?? [],
            showAbsences: typeof value.showAbsences === 'boolean' ? value.showAbsences : true
        })
    }), []);

    const handleResetAll = useCallback(() => {
        updateTimeTrackingDateRangeFilter({ startDate: defaults.startDate.format('YYYY-MM-DD'), endDate: defaults.endDate.format('YYYY-MM-DD') });
        updateTimeTrackingPropertyFilter({
            selectedProjects: [], selectedProjectTypes: [], selectedEmployees: [], selectedShiftTypes: [], selectedAbsenceTypes: [], selectedStatuses: [], showAbsences: true
        });
        searchChange('');
        handleDateRangeChange([defaults.startDate, defaults.endDate]);
    }, [defaults.endDate, defaults.startDate, handleDateRangeChange, searchChange]);

    //initial
    useEffect(() => {
        if (!dateRangeStore?.startDate || !dateRangeStore?.endDate) {
            updateTimeTrackingDateRangeFilter({
                startDate: defaults.startDate.format('YYYY-MM-DD'),
                endDate:   defaults.endDate.format('YYYY-MM-DD'),
            });
        }
    }, [dateRangeStore?.endDate, dateRangeStore?.startDate, defaults.endDate, defaults.startDate]);

    useEffect(() => {
        if (!isMobile) {
            onMobileFilterVisibilityChange?.(false);
            onMobileDateSelectorVisibilityChange?.(false);
        }
    }, [isMobile, onMobileDateSelectorVisibilityChange, onMobileFilterVisibilityChange]);

    const calendarYears = useMemo(() => years.map(x => ({
        id: x.year,
        months: x.months.map((m) => ({
            id: m.month,
            days: m.days.map((d) => ({
                id: d.day,
                type: (d.isShort ? 'short' : d.isHoliday ? 'off' : 'default') as DayType
            }))
        }))
    })), [years]);

    const dateSelectorNode = useMemo(() => (
        <ScheduleDateSelector
            selectedDates={selectedDates}
            onRangeSelected={handleDateRangeChange}
            years={calendarYears}
            minDate={today.clone().add(-365, 'days')}
            maxDate={today.clone().endOf('month')}
            allowedRangeTypes={allowedRangeTypes}
            disabled={controlsDisabled}
            compactDesktop
        />
    ), [allowedRangeTypes, calendarYears, controlsDisabled, handleDateRangeChange, selectedDates, today]);

    const handleFilterToggle = () => {
        if (controlsDisabled) {
            return;
        }
        const next = !mobileFiltersOpen;
        if (next) {
            onMobileDateSelectorVisibilityChange?.(false);
        }
        onMobileFilterVisibilityChange?.(next);
    };
    const importLabel = intl.formatMessage({ id: 'time_tracking_import_action', defaultMessage: 'Import' });
    const favoritesLabel = intl.formatMessage({ id: Lx.General.FAVORITE_FILTERS });

    const desktopSubheader = useMemo(() => (
        <div className={styles.container}>
            <Box className={styles.subheader}>
                <div className={styles.searchFilter}>
                    <SearchInput
                        searchValue={search ?? ''}
                        onChange={searchChange}
                        width='260px'
                        minWidth='82px'
                        disabled={controlsDisabled}
                    />
                    <div className={styles.filterActions}>
                        <Filter
                            onChange={handlePropertyFilterChanged}
                            hasSelected={hasSelectedEntities}
                            disabled={controlsDisabled}
                            compactNarrowDesktop
                            employees={filterEmployees}
                            projects={filterProjects}
                            projectTypes={filterProjectTypes}
                            shiftTypes={filterShiftTypes}
                            absenceTypes={filterAbsenceTypes}
                            disableEmployeesFilter={disableEmployeesFilter}
                        />
                        { hasAnyFilters && (
                            <Button className={styles.resetButton} onClick={handleResetAll} disabled={controlsDisabled}>
                                <SVGS.CancelIcon className={styles.resetIcon} />
                            </Button>
                        ) }
                    </div>
                </div>
                <div className={styles.info}>
                    <>
                        <TotalV2 totalHours={summaryHours.totalHours}
                            approved={summaryHours.approved}
                            approvableHours={summaryHours.approvable}
                            billed={summaryHours.billed}
                            absenceHours={summaryHours.absenceHours}
                            workHours={summaryHours.workHours}
                            hideTrackingHours={groupBy === TimeSheetGroupType.PROJECTVIEW || summaryHours.workHours === undefined}
                            hidePlanned={true}
                            ready={ready}/>
                        <TimeSheetGroupTool intl={intl}
                            totalHours={summaryHours.totalHours}
                            approved={0}
                            billed={0}
                            approvable={0}
                            billable={0}
                            hideButtons />
                    </>
                </div>
            </Box>
        </div>
    ), [controlsDisabled, groupBy, handlePropertyFilterChanged, handleResetAll, hasAnyFilters, hasSelectedEntities, intl, ready, search, searchChange, summaryHours]);

    if (isMobile) {
        return (
            <div className={classNames(styles.container, styles.mobileContainer)}>
                <div className={styles.mobileHeaderRow}>
                    <div className={styles.titleRow}>
                        <div className={styles.titleWithSpinner}>
                            <h3><FormattedMessage id={'timesheet_title'} /></h3>
                        </div>
                        <div className={styles.titleViewSelector}>
                            <ViewSelector value={groupBy}
                                onChange={(v) => groupByChange(v ?? TimeSheetGroupType.EMPLOYEEVIEW)}
                                disabled={controlsDisabled} />
                        </div>
                    </div>
                    <div className={styles.mobileHeaderActions}>
                        <IconButton
                            className={classNames(styles.mobileIconButton, { [styles.mobileIconButtonActive]: hasAnyFilters || mobileFiltersOpen })}
                            size={'small'}
                            onClick={handleFilterToggle}
                            disabled={controlsDisabled}
                            disableRipple
                            disableFocusRipple
                            disableTouchRipple
                        >
                            <SVGS.FilterAltRounded />
                        </IconButton>
                        {onFavoritesClick && (
                            <IconButton
                                className={styles.mobileIconButton}
                                size={'small'}
                                aria-label='favorite-filters'
                                onClick={onFavoritesClick}
                                disabled={controlsDisabled}
                                disableRipple
                                disableFocusRipple
                                disableTouchRipple
                            >
                                <SVGS.StarIcon />
                            </IconButton>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className={styles.container}>
                <Box className={styles.header}>
                    <div className={styles.headerItem}>
                        <div className={styles.titleRow}>
                            <div className={styles.titleWithSpinner}>
                                <h3><FormattedMessage id={'timesheet_title'} /></h3>
                            </div>
                            {!isMobile && ready && (
                                <div className={styles.titleViewSelector}>
                                    <ViewSelector value={groupBy}
                                        onChange={(v) => groupByChange(v ?? TimeSheetGroupType.EMPLOYEEVIEW)}
                                        disabled={controlsDisabled}/>
                                </div>
                            )}
                        </div>
                    </div>
                    <Box className={classNames(styles.dateSelector, styles.headerCalendar)}>
                        {dateSelectorNode}
                    </Box>

                    <Box className={classNames(styles.headerItem, styles.actions)}>
                        {canCreateTimeSheetItem && (
                            <Tooltip title={intl.formatMessage({ id: Lx.Schedule.CREATE_NEW_TIMESHIFT_SHIFT })}>
                                <span>
                                    <Button
                                        aria-label='create-timesheet-item'
                                        className={styles.actionButton}
                                        variant={'scheduleAction'}
                                        sx={{ height: '100%' }}
                                        onClick={onCreateTimeSheetItemClick}
                                        disabled={controlsDisabled}
                                    >
                                        <SVGS.PlusIcon className={styles.icon} />
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                        {canImport && (
                            <Tooltip title={importLabel}>
                                <span>
                                    <Button
                                        aria-label='import-time-tracking'
                                        className={styles.actionButton}
                                        variant='loadMore'
                                        sx={{ height: '100%' }}
                                        onClick={onImportClick}
                                        disabled={controlsDisabled}
                                    >
                                        <SVGS.AddChartIcon className={styles.reportIcon}/>
                                        <span className={styles.reportButtonLabel}>{importLabel}</span>
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                        {onFavoritesClick && (
                            <Tooltip title={favoritesLabel}>
                                <span>
                                    <Button
                                        aria-label='favorite-filters'
                                        className={styles.actionButton}
                                        variant='loadMore'
                                        sx={{ height: '100%' }}
                                        onClick={onFavoritesClick}
                                        disabled={controlsDisabled}
                                    >
                                        <SVGS.StarIcon className={styles.reportIcon}/>
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                    </Box>
                </Box>
            </div>
            {!isMobile && subheaderPortalContainer ? createPortal(desktopSubheader, subheaderPortalContainer) : null}
        </>
    )
}

interface TopBarMobileSectionsProps {
    years: Year[];
    today: moment.Moment;
    loading: boolean;
    summaryHours: Hours;
    mobileFiltersOpen: boolean;
    isDateSelectorOpen: boolean;
    onMobileFilterVisibilityChange: (open: boolean) => void;
    onMobileDateSelectorVisibilityChange: (open: boolean) => void;
    filterEmployees?: REmployees[];
    filterProjects?: PIdNameModel[];
    filterProjectTypes?: TiminatorType[];
    filterShiftTypes?: TiminatorType[];
    filterAbsenceTypes?: TiminatorAbsenceType[];
    disableEmployeesFilter?: boolean;
}

export const TopBarMobileSections = ({
    years,
    today,
    summaryHours,
    loading,
    mobileFiltersOpen,
    isDateSelectorOpen,
    onMobileFilterVisibilityChange,
    onMobileDateSelectorVisibilityChange,
    filterEmployees,
    filterProjects,
    filterProjectTypes,
    filterShiftTypes,
    filterAbsenceTypes,
    disableEmployeesFilter
}: TopBarMobileSectionsProps) => {
    const intl = useIntl();
    const { change: searchChange, value: search } = useTimeSheetSearch();
    const { groupBy } = useTimeSheetGroupBy();

    const dateRangeStore = useAppSelector(s => s.timeTrackingFilters.filter?.dateRangeFilter);
    const entitiesFilters = useAppSelector(s => s.timeTrackingFilters.filter?.propertyFilter);
    const employees = useAppSelector((selector) => selector.rEmployees);
    const projects = useAppSelector((selector) => selector.eProjects);
    const shiftTypes = useAppSelector((selector) => selector.types.shifts.items) as TiminatorType[];
    const absenceTypes = useAppSelector((selector) => selector.types.absences.items) as TiminatorAbsenceType[];
    const projectTypes = useAppSelector((selector) => selector.types.projects.items) as TiminatorType[];
    const resolvedEmployees = filterEmployees ?? employees;
    const resolvedProjects = filterProjects ?? projects;
    const resolvedProjectTypes = filterProjectTypes ?? projectTypes;
    const resolvedShiftTypes = filterShiftTypes ?? shiftTypes;
    const resolvedAbsenceTypes = filterAbsenceTypes ?? absenceTypes;
    const isMobile = useMediaQuery({ maxWidth: 1000 });

    const ready = loading === undefined || !loading;
    const controlsDisabled = false;
    const allowedRangeTypes = useMemo(() => ([
        RangeType.CurrentMonth,
        RangeType.PreviousMonth,
        RangeType.ThisWeek,
        RangeType.LastWeek,
        RangeType.Custom,
    ]), []);
    const defaults = useMemo(() => ({
        startDate: today.clone().startOf('month'),
        endDate: today.clone().endOf('month')
    }), [today.valueOf()]);

    const selectedDates = useMemo(() => {
        const s = dateRangeStore?.startDate
            ? moment(dateRangeStore.startDate, 'YYYY-MM-DD')
            : defaults.startDate;
        const e = dateRangeStore?.endDate
            ? moment(dateRangeStore.endDate, 'YYYY-MM-DD')
            : defaults.endDate;
        return [s, e] as moment.Moment[];
    }, [dateRangeStore, defaults]);

    const calendarYears = useMemo(() => years.map(x => ({
        id: x.year,
        months: x.months.map((m) => ({
            id: m.month,
            days: m.days.map((d) => ({
                id: d.day,
                type: (d.isShort ? 'short' : d.isHoliday ? 'off' : 'default') as DayType
            }))
        }))
    })), [years]);

    const propertyFilters = useMemo<FilterState>(() => ({
        selectedEmployees: entitiesFilters?.selectedEmployees ?? [],
        selectedProjects: entitiesFilters?.selectedProjects ?? [],
        selectedProjectTypes: entitiesFilters?.selectedProjectTypes ?? [],
        selectedShiftTypes: entitiesFilters?.selectedShiftTypes ?? [],
        selectedAbsenceTypes: entitiesFilters?.selectedAbsenceTypes ?? [],
        selectedStatuses: entitiesFilters?.selectedStatuses ?? [],
        showAbsences: typeof entitiesFilters?.showAbsences === 'boolean' ? entitiesFilters.showAbsences : true,
    }), [entitiesFilters]);

    const handleDateRangeChange = useCallback((values: moment.Moment[]) => new Promise<void>(() => {
        updateTimeTrackingDateRangeFilter({
            startDate: values[0].format('YYYY-MM-DD'),
            endDate: values[1].format('YYYY-MM-DD'),
        });
    }), []);

    const handlePropertyFilterChanged = useCallback((value: FilterState) => new Promise<void>(() => {
        updateTimeTrackingPropertyFilter({
            selectedProjects: value.selectedProjects ?? [],
            selectedProjectTypes: value.selectedProjectTypes ?? [],
            selectedEmployees: value.selectedEmployees ?? [],
            selectedShiftTypes: value.selectedShiftTypes ?? [],
            selectedAbsenceTypes: value.selectedAbsenceTypes ?? [],
            selectedStatuses: value.selectedStatuses ?? [],
            showAbsences: typeof value.showAbsences === 'boolean' ? value.showAbsences : true
        });
    }), []);

    const handleResetAll = useCallback(() => {
        updateTimeTrackingDateRangeFilter({
            startDate: defaults.startDate.format('YYYY-MM-DD'),
            endDate: defaults.endDate.format('YYYY-MM-DD')
        });
        updateTimeTrackingPropertyFilter({
            selectedProjects: [],
            selectedProjectTypes: [],
            selectedEmployees: [],
            selectedShiftTypes: [],
            selectedAbsenceTypes: [],
            selectedStatuses: [],
            showAbsences: true
        });
        searchChange('');
        handleDateRangeChange([defaults.startDate, defaults.endDate]);
    }, [defaults.endDate, defaults.startDate, handleDateRangeChange, searchChange]);

    const dateSelectorState = useMemo(() => ({
        open: isDateSelectorOpen,
        setOpen: (open: boolean) => {
            onMobileDateSelectorVisibilityChange(open);
            if (open) {
                onMobileFilterVisibilityChange(false);
            }
        }
    }), [isDateSelectorOpen, onMobileDateSelectorVisibilityChange, onMobileFilterVisibilityChange]);

    if (!isMobile) {
        return null;
    }

    return (
        <>
            <TimeSheetMobileFilterSheet
                open={controlsDisabled ? false : mobileFiltersOpen}
                onClose={() => onMobileFilterVisibilityChange(false)}
                onApply={handlePropertyFilterChanged}
                onClear={handleResetAll}
                search={search ?? ''}
                onSearchChange={searchChange}
                filters={propertyFilters}
                employees={resolvedEmployees}
                projects={resolvedProjects}
                projectTypes={resolvedProjectTypes}
                shiftTypes={resolvedShiftTypes}
                absenceTypes={resolvedAbsenceTypes}
                statuses={initialStatuses}
                disabled={controlsDisabled}
                disableEmployeesFilter={disableEmployeesFilter}
            />
            {!mobileFiltersOpen && (
                <>
                    <div className={styles.mobileDateRow}>
                        <div className={styles.mobileDateSelector}>
                            <ScheduleDateSelector
                                selectedDates={selectedDates}
                                onRangeSelected={handleDateRangeChange}
                                years={calendarYears}
                                minDate={today.clone().add(-365, 'days')}
                                maxDate={today.clone().endOf('month')}
                                allowedRangeTypes={allowedRangeTypes}
                                calendarState={dateSelectorState}
                                disabled={controlsDisabled}
                            />
                        </div>
                    </div>
                    <div className={styles.mobileSummary}>
                        <>
                            <TotalV2 totalHours={summaryHours.totalHours}
                                approved={summaryHours.approved}
                                approvableHours={summaryHours.approvable}
                                billed={summaryHours.billed}
                                absenceHours={summaryHours.absenceHours}
                                workHours={summaryHours.workHours}
                                hideTrackingHours={groupBy === TimeSheetGroupType.PROJECTVIEW || summaryHours.workHours === undefined}
                                hidePlanned={true}
                                ready={ready}/>
                            <TimeSheetGroupTool intl={intl}
                                totalHours={summaryHours.totalHours}
                                approved={0}
                                billed={0}
                                approvable={0}
                                billable={0}
                                hideButtons />
                        </>
                    </div>
                </>
            )}
        </>
    );
};
